/**
 * Global HITL entry point: a bell in the top bar polling
 * /api/approvals/pending. Before this, approvals were only reachable from
 * inside the wizard that started the run — a checkpoint could wait forever
 * if the operator closed the tab. Clicking an item opens the standard
 * ApprovalDialog. (The full approvals inbox comes with F2.)
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { api, type PendingApproval } from "../lib/api";
import { useApiQuery } from "../hooks/useApiQuery";
import { APPROVALS_POLL_MS, ID_SLICE_LEN } from "../lib/config";
import { ApprovalDialog } from "./ApprovalDialog";

export function ApprovalsBell() {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [reviewing, setReviewing] = useState<PendingApproval | null>(null);

  const pendingQuery = useApiQuery(["approvals", "pending"], api.pending, {
    pollMs: APPROVALS_POLL_MS,
  });
  const pending = pendingQuery.data ?? [];

  return (
    <>
      <Tooltip title={t("approvals.bell")}>
        <IconButton
          aria-label={t("approvals.bell")}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <Badge badgeContent={pending.length} color="warning">
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <Typography variant="overline" color="text.secondary" sx={{ px: 2 }}>
          {t("approvals.title")}
        </Typography>
        {pendingQuery.isError && (
          <MenuItem disabled>
            <Typography variant="body2" color="error">
              {t("approvals.loadFailed")}
            </Typography>
          </MenuItem>
        )}
        {!pendingQuery.isError && pending.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {t("approvals.none")}
            </Typography>
          </MenuItem>
        )}
        {pending.map((item) => (
          <MenuItem
            key={item.step_id}
            onClick={() => {
              setReviewing(item);
              setAnchorEl(null);
            }}
          >
            <ListItemText
              primary={`${item.step_key} · ${item.tool_name}`}
              secondary={`${item.dag} · ${item.job_id.slice(0, ID_SLICE_LEN)}`}
            />
          </MenuItem>
        ))}
      </Menu>
      {reviewing && (
        <ApprovalDialog
          item={reviewing}
          onClose={() => setReviewing(null)}
          onDecided={() => void pendingQuery.refetch()}
        />
      )}
    </>
  );
}
